/**
 * LiveOperators — "Already on the map": real operators that are live on Wildy today, with their
 * own photos, as proof for operators deciding whether to join. Data comes from the public
 * businesses endpoint (shared with What we offer through useLiveBusinesses). If it fails, is slow or returns too few operators, the section renders
 * nothing: we never fall back to placeholder or stock content.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { countCountries, useLiveBusinesses, type LiveBusiness } from '@/hooks/useLiveBusinesses';

interface Showcase {
  total: number;
  countries: number;
  picks: { id: string; name: string; place: string; image: string }[];
}

const SHOW = 6;
const MIN_TO_SHOW = 3;

const coverOf = (b: LiveBusiness) => (Array.isArray(b.images?.cover) ? b.images?.cover[0] : b.images?.cover);

/** Ask Cloudinary for a small, cropped, modern-format copy; other hosts are used as-is. */
const thumb = (url: string) =>
  url.includes('res.cloudinary.com') && url.includes('/upload/')
    ? url.replace('/upload/', '/upload/c_fill,g_auto,w_480,h_360,f_auto,q_auto/')
    : url;

/** One operator per country first, so the strip shows the spread rather than one region. */
function pick(list: LiveBusiness[]): Showcase['picks'] {
  const withPhotos = list.filter(b => !b.illustrativeImages && typeof coverOf(b) === 'string');
  const seen = new Set<string>();
  const firstPerCountry: LiveBusiness[] = [];
  const rest: LiveBusiness[] = [];
  for (const b of withPhotos) {
    const country = b.location?.address?.country ?? '';
    if (country && !seen.has(country)) { seen.add(country); firstPerCountry.push(b); } else rest.push(b);
  }
  return [...firstPerCountry, ...rest].slice(0, SHOW).map(b => ({
    id: b._id,
    name: b.name,
    place: [b.location?.address?.city, b.location?.address?.country].filter(Boolean).join(', '),
    image: thumb(coverOf(b) as string),
  }));
}

function useShowcase(): Showcase | 'loading' | 'none' {
  const live = useLiveBusinesses();
  if (live.state === 'loading') return 'loading';
  if (live.state === 'error') return 'none';
  const picks = pick(live.list);
  if (picks.length < MIN_TO_SHOW) return 'none';
  return { total: live.list.length, countries: countCountries(live.list), picks };
}

export function LiveOperators() {
  const { t } = useTranslation('public', { keyPrefix: 'forbusiness.live' });
  const state = useShowcase();
  const [broken, setBroken] = useState<Set<string>>(new Set());
  if (state === 'none') return null;
  const picks = state === 'loading' ? [] : state.picks.filter(op => !broken.has(op.id));

  return (
    <section aria-labelledby="live-title" className="py-16 sm:py-20 bg-white border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">{t('eyebrow')}</p>
            <h2 id="live-title" className="text-2xl sm:text-3xl font-bold text-brand-900 tracking-tight">
              {state === 'loading'
                ? <span className="inline-block h-8 w-72 max-w-full rounded bg-slate-100 animate-pulse align-middle" aria-hidden />
                : t('title', { count: state.total, countries: state.countries })}
            </h2>
          </div>
          <p className="text-slate-600 sm:text-right sm:max-w-xs">{t('description')}</p>
        </div>

        <ul className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" aria-busy={state === 'loading'}>
          {state === 'loading'
            ? Array.from({ length: SHOW }, (_, i) => (
                <li key={i} className="aspect-[4/3] rounded-lg bg-slate-100 animate-pulse" aria-hidden />
              ))
            : picks.map(op => (
                <li key={op.id}>
                  <Link
                    to={`/business/${op.id}`}
                    className="group relative block aspect-[4/3] overflow-hidden rounded-lg bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <img
                      src={op.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      onError={() => setBroken(prev => new Set(prev).add(op.id))}
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" aria-hidden />
                    <span className="absolute inset-x-0 bottom-0 p-3 sm:p-4 text-white">
                      <span className="flex items-start justify-between gap-2">
                        <span className="block font-semibold leading-snug text-sm sm:text-base line-clamp-2">{op.name}</span>
                        <ArrowUpRight className="w-4 h-4 shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" aria-hidden />
                      </span>
                      {op.place && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-white/85 truncate">
                          <MapPin className="w-3 h-3 shrink-0" aria-hidden />{op.place}
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
        </ul>
      </div>
    </section>
  );
}
